import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLang, useContent, loc, UI } from "@/lib/content";
import { api } from "@/lib/api";

export default function Pricing() {
  const { content } = useContent();
  const { lang } = useLang();
  const [buying, setBuying] = useState(false);

  const buy = async () => {
    setBuying(true);
    try {
      const r = await api("/payments/checkout", {
        method: "POST",
        body: { lookup_key: "civica_curso_completo", origin_url: window.location.origin },
      });
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(lang === "es" ? "No se pudo iniciar el pago. Intenta de nuevo." : "Could not start checkout. Please try again.");
      setBuying(false);
    }
  };

  return (
    <section id="precio" className="bg-paper py-24">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          data-testid="pricing-card"
          className="relative overflow-hidden rounded-[2rem] border border-linelight bg-navy p-8 text-center text-paper shadow-2xl sm:p-12"
        >
          <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-heritage/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full bg-brass/20 blur-3xl" />

          <span className="relative inline-flex items-center gap-2 rounded-full bg-heritage px-5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em]" data-testid="pricing-badge">
            {loc(content.price_badge, lang)}
          </span>

          <h2 className="relative mt-8 font-display text-4xl font-semibold tracking-tight sm:text-5xl">{loc(UI.pricingTitle, lang)}</h2>

          <div className="relative mt-8 flex items-end justify-center gap-4" data-testid="pricing-price">
            <span className="font-display text-3xl text-paper/40 line-through">${content.price_original}</span>
            <span className="font-display text-7xl font-semibold leading-none text-brass sm:text-8xl">${content.price_current}</span>
            <span className="pb-2 font-mono text-xs uppercase tracking-widest text-paper/60">USD</span>
          </div>

          <p className="relative mt-3 text-sm text-paper/70" data-testid="pricing-note">{loc(content.price_note, lang)}</p>

          <ul className="relative mx-auto mt-10 max-w-md space-y-3 text-left">
            {UI.pricingFeatures.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-paper/85">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brass/20 text-brass">
                  <Check size={12} strokeWidth={3} />
                </span>
                {loc(f, lang)}
              </li>
            ))}
          </ul>

          <button
            onClick={buy}
            disabled={buying}
            data-testid="pricing-cta"
            className="relative mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-heritage px-8 py-4 text-sm font-bold text-paper shadow-xl shadow-heritage/30 transition-all hover:-translate-y-0.5 hover:bg-heritagedark disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            <Lock size={15} /> {buying ? (lang === "es" ? "Abriendo pago seguro…" : "Opening secure checkout…") : loc(UI.pricingCta, lang)}
          </button>

          <p className="relative mt-5 inline-flex items-center gap-2 text-xs font-semibold text-paper/60">
            <ShieldCheck size={14} className="text-brass" /> {loc(content.hero_trust, lang)}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
